import React, { useState } from 'react';
import styles from './QuestionDetail.module.css';

interface Question {
  _id: string;
  id?: string;
  project: string;
  question: string;
  answer?: string | {
    content: string;
    attachments?: any[];
    answeredBy?: string;
    answeredAt?: string;
  };
  status: 'new' | 'in_progress' | 'answered' | 'published';
  category?: string;
  source?: 'email' | 'phone' | 'meeting' | 'social' | 'website';
  date?: string;
  author?: {
    name?: string;
    email?: string;
    phone?: string;
  };
  contactInfo?: string;
  priority?: 'high' | 'medium' | 'low';
  tags?: string[];
  internalNotes?: string;
  publishedDate?: string;
  viewCount?: number;
  helpful?: number;
  notHelpful?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

interface QuestionDetailProps {
  question: Question;
  onClose: () => void;
  onEditQuestion: (question: Question) => void;
  onPublish: (questionId: string) => void;
}

export const QuestionDetail: React.FC<QuestionDetailProps> = ({
  question,
  onClose,
  onEditQuestion,
  onPublish
}) => {
  const [activeTab, setActiveTab] = useState<'answer' | 'details' | 'history'>('answer');
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState(question);

  const statusColors = {
    new: '#FF6B6B',
    in_progress: '#FFA500',
    answered: '#277CEA',
    published: '#4CAF50'
  };

  const statusLabels = {
    new: 'Nieuw',
    in_progress: 'In behandeling',
    answered: 'Beantwoord',
    published: 'Gepubliceerd'
  };

  const priorityColors = {
    high: '#1E3A2F',
    medium: '#FFA500',
    low: '#9DA6A8'
  };

  const priorityLabels = {
    high: 'Hoog',
    medium: 'Gemiddeld',
    low: 'Laag'
  };

  const sourceIcons = {
    email: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    phone: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    meeting: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    social: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    ),
    website: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    )
  };

  const sourceLabels = {
    email: 'E-mail',
    phone: 'Telefoon',
    meeting: 'Bijeenkomst',
    social: 'Social Media',
    website: 'Website'
  };

  const handleSaveEdit = () => {
    onEditQuestion(editedQuestion);
    setIsEditing(false);
  };

  const handlePublish = () => {
    onPublish(question._id);
  };

  return (
    <div className={styles.questionDetail}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerInfo}>
            <h1>Vraag #{question._id}</h1>
            <div className={styles.headerMeta}>
              {question.category && <span className={styles.category}>{question.category}</span>}
              {question.source && (
                <span className={styles.source}>
                  {sourceIcons[question.source]}
                  {sourceLabels[question.source]}
                </span>
              )}
              {question.date && (
                <span className={styles.date}>
                  {new Date(question.date).toLocaleDateString('nl-NL')}
                </span>
              )}
            </div>
            <div className={styles.headerTags}>
              {question.tags && question.tags.map(tag => (
                <span key={tag} className={styles.tag}>{tag}</span>
              ))}
            </div>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.statusBadges}>
              <div
                className={styles.statusBadge}
                style={{ backgroundColor: statusColors[question.status] }}
              >
                {statusLabels[question.status]}
              </div>
              {question.priority && (
                <div
                  className={styles.priorityBadge}
                  style={{ backgroundColor: priorityColors[question.priority] }}
                >
                  {priorityLabels[question.priority]}
                </div>
              )}
            </div>
            <button className={styles.closeBtn} onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Quick Actions Bar */}
        <div className={styles.quickActions}>
          {question.status === 'answered' && (
            <button className={styles.quickActionBtn} onClick={handlePublish}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
              Publiceren in FAQ
            </button>
          )}
          <button
            className={styles.quickActionBtn}
            onClick={() => setIsEditing(!isEditing)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Bewerken
          </button>
          <button className={styles.quickActionBtn}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Antwoord versturen
          </button>
          <button className={styles.quickActionBtn}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Kopiëren
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'answer' ? styles.active : ''}`}
          onClick={() => setActiveTab('answer')}
        >
          Vraag & Antwoord
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'details' ? styles.active : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'history' ? styles.active : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Historie
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'answer' && (
          <div className={styles.answerTab}>
            {isEditing ? (
              <div className={styles.editForm}>
                <div className={styles.formGroup}>
                  <label>Vraag</label>
                  <textarea
                    value={editedQuestion.question}
                    onChange={(e) => setEditedQuestion({...editedQuestion, question: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Antwoord</label>
                  <textarea
                    value={typeof editedQuestion.answer === 'string'
                      ? editedQuestion.answer
                      : editedQuestion.answer?.content || ''}
                    onChange={(e) => setEditedQuestion({
                      ...editedQuestion,
                      answer: typeof editedQuestion.answer === 'object'
                        ? { ...editedQuestion.answer, content: e.target.value }
                        : e.target.value
                    })}
                    rows={6}
                  />
                </div>
                <div className={styles.formGrid}>
                  <div className={styles.formGroup}>
                    <label>Status</label>
                    <select
                      value={editedQuestion.status}
                      onChange={(e) => setEditedQuestion({...editedQuestion, status: e.target.value as Question['status']})}
                    >
                      <option value="new">Nieuw</option>
                      <option value="in_progress">In behandeling</option>
                      <option value="answered">Beantwoord</option>
                      <option value="published">Gepubliceerd</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Prioriteit</label>
                    <select
                      value={editedQuestion.priority}
                      onChange={(e) => setEditedQuestion({...editedQuestion, priority: e.target.value as Question['priority']})}
                    >
                      <option value="high">Hoog</option>
                      <option value="medium">Gemiddeld</option>
                      <option value="low">Laag</option>
                    </select>
                  </div>
                </div>
                <div className={styles.formActions}>
                  <button className={styles.btnSecondary} onClick={() => setIsEditing(false)}>
                    Annuleren
                  </button>
                  <button className={styles.btnPrimary} onClick={handleSaveEdit}>
                    Opslaan
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.questionSection}>
                  <h3>Vraag</h3>
                  <div className={styles.questionBox}>
                    <p>{question.question}</p>
                  </div>
                </div>

                <div className={styles.answerSection}>
                  <h3>Antwoord</h3>
                  <div className={styles.answerBox}>
                    {question.answer ? (
                      <p>{typeof question.answer === 'string'
                        ? question.answer
                        : question.answer.content}</p>
                    ) : (
                      <div className={styles.emptyAnswer}>
                        <p>Nog geen antwoord gegeven</p>
                        <button className={styles.btnPrimary} onClick={() => setIsEditing(true)}>
                          Antwoord toevoegen
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {question.internalNotes && (
                  <div className={styles.notesSection}>
                    <h3>Interne notities</h3>
                    <div className={styles.notesBox}>
                      <p>{question.internalNotes}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'details' && (
          <div className={styles.detailsTab}>
            <div className={styles.detailsGrid}>
              <div className={styles.detailSection}>
                <h3>Vraagsteller</h3>
                <div className={styles.detailItems}>
                  {question.author && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Naam</span>
                      <span>{typeof question.author === 'object' ? question.author?.name : question.author}</span>
                    </div>
                  )}
                  {question.contactInfo && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Contact</span>
                      <span>{question.contactInfo}</span>
                    </div>
                  )}
                  {question.source && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Bron</span>
                      <span className={styles.sourceInfo}>
                        {sourceIcons[question.source]}
                        {sourceLabels[question.source]}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3>Metadata</h3>
                <div className={styles.detailItems}>
                  {question.category && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Categorie</span>
                      <span>{question.category}</span>
                    </div>
                  )}
                  {question.date && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Ontvangen</span>
                      <span>{new Date(question.date).toLocaleDateString('nl-NL', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</span>
                    </div>
                  )}
                  {question.publishedDate && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Gepubliceerd</span>
                      <span>{new Date(question.publishedDate).toLocaleDateString('nl-NL', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</span>
                    </div>
                  )}
                  {question.viewCount !== undefined && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Weergaven</span>
                      <span>{question.viewCount}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.detailSection}>
                <h3>Tags</h3>
                <div className={styles.tagsList}>
                  {question.tags && question.tags.map(tag => (
                    <span key={tag} className={styles.tagChip}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className={styles.historyTab}>
            <h3>Activiteiten</h3>
            <div className={styles.timeline}>
              <div className={styles.timelineItem}>
                <div className={styles.timelineIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className={styles.timelineContent}>
                  <div className={styles.timelineHeader}>
                    <h4>Vraag ontvangen</h4>
                    {question.date && (
                      <span className={styles.timelineDate}>
                        {new Date(question.date).toLocaleDateString('nl-NL')}
                      </span>
                    )}
                  </div>
                  {question.source && <p>Via {sourceLabels[question.source]}</p>}
                </div>
              </div>

              {question.status === 'answered' && (
                <div className={styles.timelineItem}>
                  <div className={styles.timelineIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineHeader}>
                      <h4>Beantwoord</h4>
                      <span className={styles.timelineDate}>2 dagen geleden</span>
                    </div>
                    <p>Antwoord opgesteld en geverifieerd</p>
                  </div>
                </div>
              )}

              {question.status === 'published' && (
                <div className={styles.timelineItem}>
                  <div className={styles.timelineIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineHeader}>
                      <h4>Gepubliceerd in FAQ</h4>
                      <span className={styles.timelineDate}>
                        {question.publishedDate ? new Date(question.publishedDate).toLocaleDateString('nl-NL') : 'Vandaag'}
                      </span>
                    </div>
                    <p>Zichtbaar op de website</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};